import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DocumentUploadComponent } from 'src/app/shared/components/document-upload/document-upload.component';
import { DocumentTypeKeys, DocumentTypeLabels } from 'src/app/shared/constants-and-enums/document-types';
import { SmartDocumentUploadFileData } from 'src/app/shared/constants-and-enums/smart-document-upload-types';
import { officesForSelectionBySelectedCompany } from 'src/app/store/store';
import { CompanyMode } from '../../../constants-and-enums/company-mode.enum';
import { DocumentType } from '@iota/iota-management-portal-v4-client';

type SmartDocumentUploadFormControls = Partial<Record<keyof SmartDocumentUploadFileData, FormControl>>;

@Component({
    standalone: false,
    selector: 'app-smart-document-upload-file-upload',
    templateUrl: './smart-document-upload-file-upload.component.html',
    styleUrls: ['./smart-document-upload-file-upload.component.scss']
})
export class SmartDocumentUploadFileUploadComponent implements OnInit, OnDestroy {
    @ViewChild('uploadDocumentInput') uploadDocumentInput: DocumentUploadComponent;

    selectedFile: File;

    form = this.fb.group<SmartDocumentUploadFormControls>({
        businessPartnerNumber: new FormControl<string>(null),
        documentType: new FormControl<DocumentType>(null, { validators: [Validators.required] }),
        date: new FormControl<string>(null, { validators: [Validators.required] })
    });

    offices$ = this.store.select(officesForSelectionBySelectedCompany);

    allDocumentTypes = Object.keys(DocumentType);
    documentTypeKeyMap = DocumentTypeKeys;
    documentTypeLabelMap = DocumentTypeLabels;

    healthyWorkplaceReportDocumentType = DocumentType.HEALTHY_WORKPLACE_REPORT;

    @Input() isHwp = false;
    @Input() mode: CompanyMode;
    @Output() setNextButtonDisabled = new EventEmitter<boolean>();

    formSubscription: Subscription;

    constructor(
        private store: Store,
        private fb: FormBuilder
    ) {}

    ngOnInit(): void {
        this.initializeFormAndResetFileInput();

        this.formSubscription = this.form.statusChanges.subscribe(() => this.updateNextButtonDisabled());
    }

    ngOnDestroy(): void {
        this.formSubscription?.unsubscribe();
    }

    get showOfficeSelect() {
        return !this.isHwp && this.mode !== CompanyMode.REGULATOR;
    }

    fileChanged(files: any[]) {
        if (files?.length > 0) {
            this.selectedFile = files[0];
        } else {
            this.selectedFile = undefined;
        }
        this.updateNextButtonDisabled();
    }

    updateNextButtonDisabled() {
        this.setNextButtonDisabled.emit(this.form.status !== 'VALID' || !this.selectedFile);
    }

    getFileData(): SmartDocumentUploadFileData {
        return {
            file: this.selectedFile,
            businessPartnerNumber: this.form.controls.businessPartnerNumber.value,
            documentType: this.form.controls.documentType.value,
            date: this.form.controls.date.value
        };
    }

    initializeFormAndResetFileInput() {
        this.form.setValue({
            businessPartnerNumber: null,
            documentType: this.isHwp ? this.healthyWorkplaceReportDocumentType : null,
            // Initialize with today
            date: new Date().toISOString().substring(0, 10)
        });
        if (this.isHwp) {
            this.form.controls.documentType.disable();
        } else {
            this.form.controls.documentType.enable();
        }

        if (this.mode === CompanyMode.REGULATOR) {
            this.form.controls.businessPartnerNumber.disable();
        }

        this.uploadDocumentInput?.clear();
    }
}
